'use client';

import clsx from 'clsx';
import { useState } from 'react';

import { ColorDot, InlineButton } from '@web/ui';

import styles from './NotificationsDrawer.module.scss';

import type { NotificationInboxItem } from '@/features/notifications/types';

const EXPANDABLE_MESSAGE_LENGTH = 180;

function getExpandableText(notification: NotificationInboxItem) {
  return (
    notification.event.payload.message?.text ??
    notification.event.payload.message?.body_text ??
    ''
  );
}

export function NotificationCard({
  notification,
  message,
  timestamp,
  onClick,
}: {
  notification: NotificationInboxItem;
  message: React.ReactNode;
  timestamp: string;
  onClick: () => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isExpandable =
    getExpandableText(notification).length > EXPANDABLE_MESSAGE_LENGTH;

  return (
    <article className={styles.notificationsDrawer__card}>
      <button
        type="button"
        className={styles.notificationsDrawer__cardButton}
        onClick={onClick}
      >
        <span
          className={clsx(
            'themeAwareText',
            styles.notificationsDrawer__message,
            isExpandable &&
              !isExpanded &&
              styles.notificationsDrawer__messageCollapsed,
          )}
        >
          {message}
        </span>
        <span className={styles.notificationsDrawer__timestamp}>
          {!notification.is_read ? (
            <span
              className={styles.notificationsDrawer__unreadIndicator}
              aria-label="Unread notification"
            >
              <ColorDot color="var(--nn-color-primary)" />
            </span>
          ) : null}
          {timestamp}
        </span>
      </button>

      {isExpandable ? (
        <div className={styles.notificationsDrawer__cardActions}>
          <InlineButton
            ariaLabel={
              isExpanded ? 'Collapse notification' : 'Expand notification'
            }
            onClick={() => setIsExpanded((current) => !current)}
          >
            {isExpanded ? 'Show less' : 'Read more'}
          </InlineButton>
        </div>
      ) : null}
    </article>
  );
}
